import { scaleLeft, scaleRight, xyz, selectBox, worldQuaternion, worldPos, redMat } from '@/Interfaces/common';
import { iItemProps, Item } from '@/Engine/Item';
import { iCaseComponentsResultItem, tCaseComponentType } from '@/Interfaces/caseComponentsLib';
import { boxModelMeta, iItemModel, modelMeta } from '@/Constants/modelMeta';
import { Box3, BoxHelper, LineSegments, Vector3 } from 'three';
import { IV3D } from '@/Interfaces/v3d';
import { $ } from '@/Engine/state';
import { OBB } from 'three/examples/jsm/math/OBB';
import { getPositionXByIndexHoles, getPositionYByIndexHoles } from '@/libs/getPositionsFromHoles';
import { getMaxIndexSection } from '@/Engine/eventHandlers/libs/getHardSection';
import { faceNodeNameRGB, frameNodeNameRGB } from '@/Constants/colors';
import { useStoreUI } from '@/Store/storeUI';
declare let v3d: IV3D;

export interface iPartProps extends iItemProps {
    key: Exclude<tCaseComponentType, 'other'>;
    width: number; // В метрах
    height: number; // В метрах
    depth: number; // В метрах
    decorPosition?: xyz;
    prototypeId: string;
    indexPositionY?: number;
    indexPositionX?: number;
    isLeft: boolean;
    isDefaultHolder?: boolean;
}
export type tKeyType = Exclude<Exclude<tCaseComponentType, 'other'>, 'drawerSeparator'>;
export class Part extends Item {
    key: tKeyType;
    width: number;
    height: number;
    depth: number;
    decorPosition?: xyz;
    prototypeId: string;
    indexPositionY?: number;
    indexPositionX?: number;
    isLeft: boolean;
    selectBox: LineSegments | BoxHelper | null | undefined;
    obb: OBB|null = new OBB();
    box: Box3 = new Box3();
    parentAccessory?: Part[] = [];
    childrenAccessory: Part[] = [];
    //комплектующее на всю ширину шкафа или нет
    isSmall = false;
    //приоритет при наложении комплектующих  (для экранов)
    isPriorityIndex = 0;
    isDefaultHolder?: boolean;
    _boxInit?: boxModelMeta;
    constructor({
                    key,
                    id,
                    width,
                    height,
                    depth,
                    decorPosition,
                    model,
                    scale,
                    offset,
                    rotation,
                    prototypeId,
                    indexPositionY,
                    indexPositionX,
                    isLeft,
                    isDefaultHolder,
    }: iPartProps) {
        super({ model, scale, offset, rotation, id });
        this.key = key;
        this.width = width;
        this.height = height;
        this.depth = depth;
        this.decorPosition = decorPosition;
        this.prototypeId = prototypeId;
        this.isLeft = isLeft;
        this.indexPositionY = indexPositionY;
        this.indexPositionX = indexPositionX;
        this.isDefaultHolder = isDefaultHolder;
        this._boxInit = modelMeta.find(x => this.model.name === x.name)?.box;
        if (!this._boxInit || !this._boxInit.width || !this._boxInit.height || !this._boxInit.depth) return;
        this.createSelectedBox(this._boxInit);
        this.createBox3(this._boxInit);
    }

    getBoxHolderInChildren(): Part|undefined {
        return this.model.children
            .find(x => x?.name.includes('plastic_box_holder'))?.userData?.parent    ;
    }
    getTypesParentAccessory() {
        return this.parentAccessory?.map(x => x.key);
    }
    isPlasticBoxWithParentShield() {
        return this._model?.name.includes('plastic_box_400x230x150') &&
            this.getTypesParentAccessory()?.includes('shield');
    }
    isPlasticBoxWithParentShelf() {
        return this._model?.name.includes('plastic_box_400x230x150') &&
            this.getTypesParentAccessory()?.includes('shelf');
    }
    isChangeChpuHard() {
        return this.model.name?.includes('chpu_hard') && getMaxIndexSection(this.model) === 1;
    }

    getMaxPositionY() {
        //максимальное(верхнее) фактическое значение по y
        if (!this._boxInit) return null;
        return this.position.y + this._boxInit.offset[1] + this._boxInit.height;
    }
    get raw(): iCaseComponentsResultItem {
        const res: iCaseComponentsResultItem = {
            id: this.id,
            prototypeId: this.prototypeId,
            isLeft: this.isLeft,
            isDefaultHolder: this.isDefaultHolder,
        };
        if (this.indexPositionY !== undefined) res.indexPositionY = this.indexPositionY;
        if (this.indexPositionX !== undefined) res.indexPositionX = this.indexPositionX;
       return res;
    }
    set _isLeft(flag: boolean) {
        this.isLeft = flag;
        this.scale = new Vector3(this.isLeft ? -1 : 1,1,1);
    }
    set _indexPositionY(posY: number) {
        this.indexPositionY = posY;
        const pos: number|undefined = getPositionYByIndexHoles(this, this.model.parent?.name);
        if (!pos) return;
        this.position = new Vector3(this.position.x, pos, this.position.z);
    }

    set _indexPositionX(posX: number) {
        this.indexPositionX = posX;
        const pos: number|undefined = getPositionXByIndexHoles(this, this.model.parent?.name);
        if (!pos) return;
        this.position = new Vector3(pos, this.position.y, this.position.z);
    }

    redMark() {
        this._model.material = redMat;
    }
    isRedMark() {
        return this._model.material === redMat;
    }
    noMark() {
        this._model.material = this.materials[0];
    }
    set scale(scale: Vector3) {
        super.scale = scale;
        this.updObb();
    }
    get scale() {
        return super.scale;
    }

    set position(position: Vector3) {
        super.position = position;
        this.updObb();
    }
    get position() {
        return super.position;
    }
    unselect() {
        if (this.selectBox) this.model?.remove(this.selectBox);
        $.selectedModel = null;
        useStoreUI().isSelectedModel = false;
    }
    select() {
        if (!this.selectBox) return;
        $.selectedModel = this.model;
        useStoreUI().isSelectedModel = true;
        this.positionStart.copy(this.model.position);
        this.box.getCenter(this.selectBox?.position);
        this.model.add(this.selectBox);
    }
    settingPart(position, isLeft, colorFrame, colorFace) {
        this.position = position;
        this._isLeft = isLeft;
        this.setColor(frameNodeNameRGB, colorFrame);
        this.setColor(faceNodeNameRGB, colorFace);
        $.toolChest?.addComponent(this);
    }
    createBox3(box: Omit<iItemModel, 'name'> & {isSmall?: boolean}) {
        if (!box || !box.width || !box.height || !box.depth) return;
        this.isSmall = box.isSmall ?? false;
        const correctWidth = this.isSmall ?  box.width : box.width / 2;
        const max = new Vector3(
            correctWidth + box.offset[0],
            box.height + box.offset[1],
            box.depth + box.offset[2],
        );
        const min = new Vector3(
            max.x - box.width,
            box.offset[1],
            box.offset[2],
        );
        this.box = new Box3(min, max);
    }
    createSelectedBox(box: Omit<iItemModel, 'name'> & {isSmall?: boolean}) {
        this.selectBox = selectBox.clone();
        this.selectBox.scale.set(box.width ?? 1, box.height ?? 1, box.depth ?? 1);
        this.selectBox.name = 'box';
    }
    updObb() {
        if (this.box.isEmpty()) {
            console.error('Не определен Box3 (не задан box для ', this.model.name, ' )');
            return;
        }
        const scale = new Vector3();
        this.model.getWorldQuaternion(worldQuaternion);
        this.model.getWorldScale(scale);

        const sizes = new Vector3();
        const center = new Vector3();

        this.box.getSize(sizes);
        sizes.applyQuaternion(worldQuaternion);
        sizes.multiply(scale);
        this.box.getCenter(center);
        center.applyQuaternion(worldQuaternion);
        center.multiply(scale);

        const sizeHalf = new Vector3(
            Math.abs(sizes.x/2),
            Math.abs(sizes.y/2),
            Math.abs(sizes.z/2),
        );

        this.model.getWorldPosition(worldPos);
        const obbCenter = new Vector3(
            worldPos.x + center.x,
            worldPos.y + center.y,
            worldPos.z + center.z,
        );

        if (!this.obb) this.obb = new OBB();
        this.obb.center.copy(obbCenter);
        this.obb.halfSize.copy(sizeHalf);

    }
}